import type { MktSeoBewegung, MktUebersicht, MktUebersichtHinweis, MktUebersichtKpi } from '@portal/contracts';
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { HttpError, marketingApi } from '../../api.js';
import { gesternBerlin, istVorlaeufig, QUELLE_STATUS, tagDeutsch, VORLAEUFIG_HINWEIS } from './marketing-ui.js';
import {
  achsenMax, anteilText, deltaUe, freiPruefen, geladenText, herkunftBelegt, istWochenende, kpiWert, plattformenWartenText, presetZeitraum, quellenAngebunden, quellenWartend,
  saeuleText, segmentZeitraum, seoBewegung, tageZwischen, tagPruefen, tagTTMM, UE_MODI, UE_PRESETS, vergleichText, werteBisText, wertText, zeitraumText, zeitraumVorlaeufig,
  type UeModus, type UePreset, type UeZeitraum,
} from './uebersicht-ui.js';

type Stand = { kind: 'laden'; alt: MktUebersicht | null } | { kind: 'fehler'; status: number; alt: MktUebersicht | null } | { kind: 'ok'; daten: MktUebersicht };

/** #901b Übersicht lädt `/uebersicht?von&bis`; beim Wechsel bleibt der letzte Stand sichtbar (aria-busy), bis die Antwort da ist */
function useUebersicht(z: UeZeitraum): Stand {
  const [stand, setStand] = useState<Stand>({ kind: 'laden', alt: null });
  useEffect(() => {
    let lebt = true;
    setStand((s) => ({ kind: 'laden', alt: s.kind === 'ok' ? s.daten : s.alt }));
    marketingApi.uebersicht(z).then(
      (daten) => lebt && setStand({ kind: 'ok', daten }),
      (err: unknown) => lebt && setStand((s) => ({ kind: 'fehler', status: err instanceof HttpError ? err.status : 0, alt: s.kind === 'ok' ? s.daten : s.alt })),
    );
    return () => {
      lebt = false;
    };
  }, [z.von, z.bis]); // eslint-disable-line react-hooks/exhaustive-deps
  return stand;
}

const BERICHTSTAG = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Europe/Berlin' });

/**
 * #901b Marketing-Übersicht nach PD-Layoutkonzept #901: Zeitraum → KPI-Kacheln → Verlauf → Herkunft → Umsatz + Einstiegsseiten →
 * SEMrush → Zu wissen → Quellen/Freigaben. Alle Werte rechnet die API; hier nur Anzeige.
 */
export function Uebersicht() {
  const gestern = gesternBerlin();
  const [modus, setModus] = useState<UeModus>('gestern');
  const [tag, setTag] = useState(gestern);
  const [frei, setFrei] = useState<UeZeitraum>(() => segmentZeitraum('30', gestern));
  const z = modus === 'frei' ? frei : segmentZeitraum(modus, gestern, tag);
  const stand = useUebersicht(z);
  const d = stand.kind === 'ok' ? stand.daten : stand.alt;

  return (
    <div className="mku" aria-busy={stand.kind === 'laden'}>
      <div className="mku-titel">
        <div>
          <h1 className="ap-title">Marketing im Blick</h1>
          <p className="mku-muted">Was Menschen zu uns führt. Und was daraus entsteht.</p>
        </div>
        <div className="mku-stand">
          <div className="mku-eyebrow">Berichtsstand</div>
          <div>{BERICHTSTAG.format(new Date())}</div>
          <div className="mku-meta">Zahlen bis gestern · {tagTTMM(gestern)}</div>
        </div>
      </div>

      <Zeitraumkopf modus={modus} setModus={setModus} tag={tag} setTag={setTag} frei={frei} setFrei={setFrei} gestern={gestern} z={z}
        historieAb={d?.historieAb ?? null} vergleich={d && stand.kind === 'ok' ? vergleichText(d.vergleich) : null} />

      {stand.kind === 'laden' && !d && <p className="sh-status" role="status">Wird geladen …</p>}
      {stand.kind === 'fehler' && (
        <p className="marketing-fehler" role="alert">{stand.status === 503 ? 'Noch nicht eingerichtet.' : stand.status === 400 ? 'Ungültiger Zeitraum.' : 'Übersicht konnte nicht geladen werden.'}</p>
      )}
      {d && <Inhalt d={d} gestern={gestern} />}
    </div>
  );
}

// ── Zeitraumkopf ───────────────────────────────────────────────────────────────────────────────────────────────────
function Zeitraumkopf(p: {
  modus: UeModus; setModus: (m: UeModus) => void; tag: string; setTag: (t: string) => void; frei: UeZeitraum; setFrei: (z: UeZeitraum) => void;
  gestern: string; z: UeZeitraum; historieAb: string | null; vergleich: string | null;
}) {
  const { modus, gestern, z } = p;
  const [tagText, setTagText] = useState(tagDeutsch(p.tag));
  const [tagFehler, setTagFehler] = useState<string | null>(null);
  const [von, setVon] = useState(tagDeutsch(p.frei.von));
  const [bis, setBis] = useState(tagDeutsch(p.frei.bis));
  const [freiFehler, setFreiFehler] = useState<string | null>(null);
  const [preset, setPreset] = useState<UePreset | null>('30');

  const modusWaehlen = (m: UeModus) => {
    setTagFehler(null);
    setFreiFehler(null);
    if (m === 'gestern') { p.setTag(gestern); setTagText(tagDeutsch(gestern)); }
    if (m === 'frei') { setVon(tagDeutsch(p.frei.von)); setBis(tagDeutsch(p.frei.bis)); }
    p.setModus(m);
  };
  const tagUebernehmen = () => {
    const r = tagPruefen(tagText, gestern);
    if (typeof r === 'string') return setTagFehler(r);
    setTagFehler(null);
    if (r.tag !== p.tag) p.setTag(r.tag);
  };
  const anwenden = (vonT: string, bisT: string, pr: UePreset | null) => {
    const r = freiPruefen(vonT, bisT, gestern, p.historieAb);
    if (typeof r === 'string') return setFreiFehler(r);
    setFreiFehler(null);
    setPreset(pr);
    if (r.von !== p.frei.von || r.bis !== p.frei.bis) p.setFrei(r);
  };
  const presetWaehlen = (pr: UePreset) => {
    const r = presetZeitraum(pr, gestern, p.historieAb);
    if (typeof r === 'string') return setFreiFehler(r);
    setVon(tagDeutsch(r.von));
    setBis(tagDeutsch(r.bis));
    anwenden(tagDeutsch(r.von), tagDeutsch(r.bis), pr);
  };
  const absenden = (e: FormEvent) => {
    e.preventDefault();
    anwenden(von, bis, null);
  };

  return (
    <section className="mku-zeitraum" aria-label="Berichtszeitraum">
      <div className="mku-steuerzeile">
        <div className="marketing-zeitraum" role="group" aria-label="Kennzahlen-Zeitraum">
          {UE_MODI.map((m) => (
            <button key={m.modus} type="button" className="marketing-schalter" aria-pressed={modus === m.modus} onClick={() => modusWaehlen(m.modus)}>{m.label}</button>
          ))}
        </div>
        {modus === 'gestern' && (
          <label className="mku-feld mku-feld--tag">
            <span>Tagesauswertung</span>
            <input type="text" inputMode="numeric" lang="de" autoComplete="off" placeholder="TT.MM.JJJJ" maxLength={10} className="marketing-tagwahl__eingabe"
              value={tagText} aria-invalid={tagFehler ? true : undefined} aria-describedby={tagFehler ? 'mku-tag-fehler' : undefined}
              onChange={(e) => setTagText(e.target.value)} onBlur={tagUebernehmen} onKeyDown={(e) => { if (e.key === 'Enter') tagUebernehmen(); }} />
          </label>
        )}
      </div>
      {modus === 'gestern' && tagFehler && <p id="mku-tag-fehler" className="mku-fehler" role="alert">{tagFehler}</p>}
      {modus === 'frei' && (
        <div className="mku-frei">
          <div className="marketing-zeitraum" role="group" aria-label="Zeitraum-Vorlagen">
            {UE_PRESETS.map((x) => (
              <button key={x.preset} type="button" className="marketing-schalter" aria-pressed={preset === x.preset} onClick={() => presetWaehlen(x.preset)}>{x.label}</button>
            ))}
          </div>
          <form className="mku-frei__form" noValidate onSubmit={absenden}>
            <label className="mku-feld">
              <span>Von</span>
              <input type="text" inputMode="numeric" lang="de" autoComplete="off" placeholder="TT.MM.JJJJ" maxLength={10} className="marketing-tagwahl__eingabe" value={von}
                aria-invalid={freiFehler ? true : undefined} aria-describedby={freiFehler ? 'mku-frei-fehler' : undefined} onChange={(e) => setVon(e.target.value)} />
            </label>
            <label className="mku-feld">
              <span>Bis</span>
              <input type="text" inputMode="numeric" lang="de" autoComplete="off" placeholder="TT.MM.JJJJ" maxLength={10} className="marketing-tagwahl__eingabe" value={bis}
                aria-invalid={freiFehler ? true : undefined} aria-describedby={freiFehler ? 'mku-frei-fehler' : undefined} onChange={(e) => setBis(e.target.value)} />
            </label>
            <button type="submit" className="marketing-schalter mku-anwenden">Zeitraum anwenden</button>
          </form>
          {freiFehler && <p id="mku-frei-fehler" className="mku-fehler" role="alert">{freiFehler}</p>}
        </div>
      )}
      <p className="mku-info">
        <span>{zeitraumText(z, gestern)}</span>
        {zeitraumVorlaeufig(z) && <span className="mku-badge" title={VORLAEUFIG_HINWEIS}>vorläufig bis T+3<span className="sr-only"> – {VORLAEUFIG_HINWEIS}</span></span>}
        {p.vergleich && <span>{p.vergleich}</span>}
      </p>
    </section>
  );
}

// ── Inhalt ─────────────────────────────────────────────────────────────────────────────────────────────────────────
function Inhalt({ d, gestern }: { d: MktUebersicht; gestern: string }) {
  return (
    <>
      <section aria-label="Kennzahlen">
        <div className="mku-kpis">{d.kpis.map((k) => <Kachel key={k.key} k={k} />)}</div>
        <p className="mku-fussnote">
          Sitzungen sind keine eindeutigen Personen. Shopify und GA4 werden getrennt ausgewiesen.
          {!d.vollstaendig && ' Zeitraum oder Vergleichsperiode nicht lückenlos geliefert — keine Hochrechnung, kein Vergleich unvollständiger Perioden.'}
        </p>
      </section>
      <Verlauf d={d} gestern={gestern} />
      <Herkunft d={d} />
      <div className="mku-zweispaltig">
        <Umsatz d={d} gestern={gestern} />
        <Einstieg d={d} />
      </div>
      <Seo d={d} />
      <ZuWissen hinweise={d.hinweise} />
      <Quellen d={d} />
    </>
  );
}

function Kachel({ k }: { k: MktUebersichtKpi }) {
  const w = kpiWert(k);
  const dl = deltaUe(k.delta, k.neutral);
  const geld = k.format === 'euro';
  return (
    <article className={`mku-kpi${k.key === 'sessionsShopify' || k.key === 'umsatz' ? ' mku-kpi--haupt' : ''}`}>
      <h3 className="mku-kpi__label">{k.label}</h3>
      <p className="mku-kpi__quelle">{k.quelle}</p>
      <p className={`mku-kpi__wert${geld ? ' mku-kpi__wert--geld' : ''}${w.istHinweis ? ' mku-kpi__wert--hinweis' : ''}`}>{w.text}</p>
      <p className={`mku-delta mku-delta--${dl.ton}`}>
        <span aria-hidden="true">{dl.pfeil && `${dl.pfeil} `}{dl.text}{k.neutral && k.delta !== null ? ' · Kosten' : ''}</span>
        <span className="sr-only">{dl.sr}{k.neutral && k.delta !== null ? ' (Kosten, neutral bewertet)' : ''}</span>
      </p>
    </article>
  );
}

function Kopf({ id, titel, meta, badge }: { id: string; titel: string; meta: string; badge?: string }) {
  return (
    <div className="mku-kopf">
      <div>
        <h2 id={id} className="mku-h2">{titel}</h2>
        <p className="mku-meta">{meta}</p>
      </div>
      {badge && <span className="mku-badge">{badge}</span>}
    </div>
  );
}

function useBreite<T extends HTMLElement>(start: number): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [b, setB] = useState(start);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setB(el.clientWidth || start);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setB(el.clientWidth || start));
    ro.observe(el);
    return () => ro.disconnect();
  }, [start]);
  return [ref, b];
}

function Verlauf({ d, gestern }: { d: MktUebersicht; gestern: string }) {
  const [metrik, setMetrik] = useState<'sessions' | 'umsatz'>('sessions');
  const [live, setLive] = useState('Einzelne Tage im Diagramm auswählen.');
  const [ref, breite] = useBreite<HTMLDivElement>(600);
  const v = d.verlauf;
  const tage = v.tage;
  const werte = tage.map((t) => t[metrik]);
  const w = Math.max(290, breite);
  const h = 210, links = 40, rechts = 10, oben = 12, unten = 31;
  const pw = w - links - rechts, ph = h - oben - unten;
  const max = achsenMax(werte, metrik);
  const y = (x: number) => oben + ph - (x / max) * ph;
  const schritt = pw / Math.max(1, tage.length);
  const schnitt = v.schnitt30[metrik];
  const summe = werte.reduce<number>((a, x) => a + (x ?? 0), 0);
  const fehlend = werte.filter((x) => x === null).length;
  const jedes = Math.max(1, Math.ceil((tage.length - 1) / 4));
  const fmt = (x: number | null) => (metrik === 'umsatz' ? wertText(x, 'euro') : wertText(x, 'zahl'));
  const achse = (x: number) => (x >= 1000 ? `${wertText(x / 1000, 'zahl')}k` : wertText(x, 'zahl'));
  const spanne = tageZwischen(v.von, v.bis);
  return (
    <section className="pb-card mku-panel" aria-labelledby="mku-verlauf">
      <div className="mku-kopf">
        <div>
          <h2 id="mku-verlauf" className="mku-h2">Wie sich die Nachfrage entwickelt</h2>
          <p className="mku-meta">{spanne === 30 ? '30 Tage' : 'Gewählter Zeitraum'} · {tagDeutsch(v.von)} – {tagDeutsch(v.bis)} · Shopify</p>
        </div>
        <div className="marketing-zeitraum" role="group" aria-label="Diagramm-Kennzahl">
          <button type="button" className="marketing-schalter" aria-pressed={metrik === 'sessions'} onClick={() => setMetrik('sessions')}>Sitzungen</button>
          <button type="button" className="marketing-schalter" aria-pressed={metrik === 'umsatz'} onClick={() => setMetrik('umsatz')}>Umsatz</button>
        </div>
      </div>
      <div className="mku-diagramm-summe">
        <div><strong>{fmt(summe)}</strong><span>{metrik === 'umsatz' ? 'Umsatz' : 'Sitzungen'} im Diagramm{fehlend ? ` · ${fehlend} ${fehlend === 1 ? 'Tag' : 'Tage'} ohne Werte` : ''}</span></div>
        <div><strong>{schnitt === null ? '—' : fmt(Math.round(schnitt))}</strong><span>{schnitt === null ? 'Schnitt fehlt · letzte 30 Tage unvollständig' : 'Ø pro Tag · letzte 30 Tage'}</span></div>
      </div>
      <div ref={ref} className="mku-diagramm-rahmen">
        <svg className="mku-diagramm" viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="group" aria-label="Täglicher Verlauf, Werte per Fokus oder Zeigen erreichbar">
          {[0, 1, 2, 3, 4].map((q) => {
            const val = (max * q) / 4;
            return (
              <g key={q} aria-hidden="true">
                <line className="mku-gitter" x1={links} x2={w - rechts} y1={y(val)} y2={y(val)} />
                <text textAnchor="end" x={links - 7} y={y(val) + 4}>{achse(val)}</text>
              </g>
            );
          })}
          {tage.map((t, i) => (istWochenende(t.tag) ? <rect key={`we-${t.tag}`} className="mku-wochenende" aria-hidden="true" x={links + i * schritt} y={oben} width={schritt} height={ph} /> : null))}
          {tage.map((t, i) => {
            const x = links + i * schritt + Math.max(0.5, schritt * 0.16);
            const bw = Math.max(1, schritt * 0.68);
            const val = t[metrik];
            const text = saeuleText(t.tag, val, metrik, v.bis);
            const zeigen = () => setLive(text);
            if (val === null) {
              return <rect key={t.tag} className="mku-luecke" tabIndex={0} role="img" aria-label={text} x={x} y={oben} width={bw} height={ph} onFocus={zeigen} onMouseEnter={zeigen} onClick={zeigen}><title>{text}</title></rect>;
            }
            return (
              <rect key={t.tag} className={`mku-saeule${t.tag === v.bis ? ' mku-saeule--letzter' : ''}`} tabIndex={0} role="img" aria-label={text}
                x={x} y={y(val)} width={bw} height={Math.max(0, oben + ph - y(val))} rx={1} onFocus={zeigen} onMouseEnter={zeigen} onClick={zeigen}>
                <title>{text}</title>
              </rect>
            );
          })}
          {tage.map((t, i) => {
            const letzter = i === tage.length - 1;
            if (!(i === 0 || letzter || (i % jedes === 0 && i < tage.length - 3))) return null;
            const x = links + i * schritt + schritt / 2;
            return <text key={`x-${t.tag}`} aria-hidden="true" textAnchor={i === 0 ? 'start' : letzter ? 'end' : 'middle'} x={i === 0 ? links : letzter ? w - rechts : x} y={h - 7}>{tagTTMM(t.tag)}</text>;
          })}
          {schnitt !== null && <line className="mku-schnitt" aria-hidden="true" x1={links} x2={w - rechts} y1={y(schnitt)} y2={y(schnitt)} />}
        </svg>
      </div>
      <div className="mku-legende" aria-hidden="true">
        <span><i className="mku-legende__gold" />{v.bis === gestern ? 'Gestern' : `Letzter Tag ${tagTTMM(v.bis)}`}</span>
        <span><i className="mku-legende__blau" />Wochenende</span>
        <span><i className="mku-legende__strich" />30-Tage-Schnitt</span>
        {fehlend > 0 && <span>Lücke = keine Werte geliefert</span>}
      </div>
      <p className="mku-live" aria-live="polite">{live}</p>
      <details className="mku-details">
        <summary>Werte als Tabelle ansehen</summary>
        <div className="mku-tabellenrahmen mku-tabellenrahmen--hoch" tabIndex={0} role="region" aria-label="Tageswerte">
          <table className="mku-tabelle">
            <thead><tr><th scope="col">Tag</th><th scope="col">Sitzungen</th><th scope="col">Umsatz</th></tr></thead>
            <tbody>
              {tage.map((t) => (
                <tr key={t.tag}><td>{tagDeutsch(t.tag)}</td><td>{wertText(t.sessions, 'zahl')}</td><td>{wertText(t.umsatz, 'euro')}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

const freigabenOeffnen = () => {
  const el = document.getElementById('mku-freigaben');
  if (el instanceof HTMLDetailsElement) el.open = true;
};

function Herkunft({ d }: { d: MktUebersicht }) {
  const h = d.herkunft;
  const gruppen = herkunftBelegt(h.gruppen);
  return (
    <section className="pb-card mku-panel" aria-labelledby="mku-herkunft">
      <Kopf id="mku-herkunft" titel="Woher unsere Besucher kommen" meta={`Sitzungen · GA4-Kanalgruppen · 7 / 30 Tage bis ${tagDeutsch(h.bis)}`} badge="Anteil an allen Sitzungen · 7 T" />
      <table className="mku-tabelle mku-herkunft">
        <caption className="sr-only">Herkunft der Sitzungen nach Kanalgruppe, 7 und 30 Tage</caption>
        <thead><tr><th scope="col">Herkunft</th><th scope="col">7 Tage</th><th scope="col">Anteil</th><th scope="col">30 Tage</th></tr></thead>
        <tbody>
          {gruppen.length === 0 && <tr className="mku-warten"><td colSpan={4}>Noch keine Herkunftsdaten in diesem Zeitfenster.</td></tr>}
          {gruppen.map((g, i) => (
            <tr key={g.key}>
              <th scope="row"><div className="mku-kanal"><span className="mku-punkt" style={{ opacity: Math.max(0.3, 1 - i * 0.11) }} aria-hidden="true" /><span>{g.label}<small>{g.beschreibung}</small></span></div></th>
              <td data-label="7 Tage">{wertText(g.s7, 'zahl')}</td>
              <td data-label="Anteil · 7 T">{anteilText(g.anteil7)}<div className="mku-anteilbalken" aria-hidden="true"><b style={{ width: `${Math.min(100, (g.anteil7 ?? 0) * 100)}%` }} /></div></td>
              <td data-label="30 Tage">{wertText(g.s30, 'zahl')}</td>
            </tr>
          ))}
          {h.wartend.map((x) => (
            <tr key={x.system} className="mku-warten">
              <td colSpan={4}>
                {x.name} <span className="mku-badge">wartet auf Freigabe</span>
                <small>{x.hinweis} · <a href="#mku-freigaben" onClick={freigabenOeffnen}>Freigaben ansehen</a></small>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mku-fussnote">Kanäle mit Messwerten; ohne Daten keine Zahlenzeile. Erkannte KI-Verweise sind eine Untergrenze. Plattform-Freigaben sind davon unabhängig.</p>
    </section>
  );
}

function Umsatz({ d, gestern }: { d: MktUebersicht; gestern: string }) {
  const u = d.umsatz;
  const unvollstaendig = u.vollstaendig.some((x) => !x);
  return (
    <section className="pb-card mku-panel" aria-labelledby="mku-umsatz">
      <Kopf id="mku-umsatz" titel="Aus Besuchen werden Bestellungen" meta={`Shopify · feste Zeitfenster bis ${tagDeutsch(u.bis)}`} />
      <div className="mku-tabellenrahmen" role="region" aria-label="Umsatz und Bestellungen" tabIndex={0}>
        <table className="mku-tabelle mku-umsatztabelle">
          <thead>
            <tr>
              <th scope="col">Kennzahl</th>
              <th scope="col">Tag {tagTTMM(u.bis)}{u.bis === gestern && <span className="sr-only"> (gestern)</span>}</th>
              <th scope="col">7 T</th>
              <th scope="col">30 T</th>
            </tr>
          </thead>
          <tbody>
            {u.zeilen.map((z) => (
              <tr key={z.key}>
                <th scope="row">{z.label}</th>
                {z.werte.map((x, i) => <td key={i}>{wertText(x, z.format === 'euro' && z.key === 'bestellwert' ? 'euro2' : z.format)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mku-fussnote">
        {unvollstaendig ? 'Nicht alle Zeitfenster sind lückenlos geliefert („—“). Keine Hochrechnung. ' : ''}
        Conversion = Bestellungen / Shopify-Sitzungen. Ø Bestellwert = Umsatz / Bestellungen; keine Mittelung von Tagesquoten.
      </p>
    </section>
  );
}

function Einstieg({ d }: { d: MktUebersicht }) {
  const e = d.einstieg;
  return (
    <section className="pb-card mku-panel" aria-labelledby="mku-einstieg">
      <Kopf id="mku-einstieg" titel="Hier beginnt der Besuch" meta={`Top-Einstiegsseiten · 7 Tage bis ${tagDeutsch(e.bis)} · GA4`} badge="Sitzungen" />
      {e.seiten.length === 0 ? <p className="mku-leer">Noch keine Einstiegsseiten geliefert.</p> : (
        <ol className="mku-einstieg">
          {e.seiten.slice(0, 5).map((s, i) => (
            <li key={s.pfad}>
              <span className="mku-rang" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
              <span className="mku-pfad">{s.pfad}</span>
              <span className="mku-zahl">{wertText(s.sessions, 'zahl')}<span className="sr-only"> Sitzungen</span></span>
            </li>
          ))}
        </ol>
      )}
      <p className="mku-fussnote">Seitenpfade ohne URL-Parameter. Besuche sind keine Kaufzuordnung.</p>
    </section>
  );
}

function Seo({ d }: { d: MktUebersicht }) {
  const s = d.seo;
  const [ansicht, setAnsicht] = useState<'bewegung' | 'schwelle'>('bewegung');
  const meta = s.woche ? `SEMrush · Deutschland · Stand ${tagDeutsch(s.woche)}${s.vergleichWoche ? ` · Vergleich ${tagDeutsch(s.vergleichWoche)}` : ' · noch kein Vergleichsstand'}` : 'SEMrush · noch kein Stand';
  const listen: [string, MktSeoBewegung[]][] = ansicht === 'bewegung' ? [['Gewinner', s.gewinner], ['Verlierer', s.verlierer]] : [['Neu in Top 20', s.neuTop20], ['Aus Top 10', s.ausTop10]];
  return (
    <section className="pb-card mku-panel" aria-labelledby="mku-seo">
      <Kopf id="mku-seo" titel="Sichtbarkeit bei Google" meta={meta} badge={`Nur montags${s.naechsterStand ? ` · nächster Stand ${tagTTMM(s.naechsterStand)}` : ''}`} />
      {!s.woche ? <p className="mku-leer">Noch kein SEMrush-Stand geliefert.</p> : (
        <>
          <div className="marketing-zeitraum" role="group" aria-label="Keyword-Ansicht">
            <button type="button" className="marketing-schalter" aria-pressed={ansicht === 'bewegung'} onClick={() => setAnsicht('bewegung')}>Gewinner &amp; Verlierer</button>
            <button type="button" className="marketing-schalter" aria-pressed={ansicht === 'schwelle'} onClick={() => setAnsicht('schwelle')}>Neu in Top 20 / aus Top 10</button>
          </div>
          <div className="mku-seo-raster">
            {listen.map(([titel, liste]) => (
              <div key={titel}>
                <h3 className="mku-minikopf">{titel} · {liste.length}</h3>
                {liste.length === 0 ? <p className="mku-leer">Keine Bewegung in dieser Liste.</p> : (
                  <ul className="mku-seo-liste">
                    {liste.map((k) => {
                      const b = seoBewegung(k.vorher, k.jetzt);
                      return (
                        <li key={k.keyword} className="mku-seo-zeile">
                          <span>{k.keyword}<small>Position {k.vorher ?? '—'} → {k.jetzt ?? '—'}</small></span>
                          <span className={`mku-positionen mku-delta--${b.ton}`}><strong aria-hidden="true">{b.text}</strong><span className="sr-only">{b.sr}</span><small aria-hidden="true">Positionen</small></span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </>
      )}
      <p className="mku-fussnote">Positionen, keine Besucherzahlen. Nur belegte Positionspaare; „nicht mehr im Ausschnitt“ ist kein nachgewiesener Ranking-Verlust.</p>
    </section>
  );
}

const HINWEIS_ART: Record<MktUebersichtHinweis['art'], string> = { abweichung: 'Abweichung über 30 %', veraltet: 'Quelle veraltet', null_lieferung: 'Gemeldete 0-Lieferung' };

function ZuWissen({ hinweise }: { hinweise: MktUebersichtHinweis[] }) {
  return (
    <section className="pb-card mku-panel" aria-labelledby="mku-wissen">
      <Kopf id="mku-wissen" titel="Zu wissen" meta="Auffälligkeiten aus vorhandenen Werten · keine Ursachenbehauptungen" badge="Regelvorschlag" />
      {hinweise.length === 0 ? <p className="mku-leer">Keine Auffälligkeiten nach den Regeln.</p> : (
        <div className="mku-hinweise">
          {hinweise.map((x, i) => (
            <article key={i} className="mku-hinweis">
              <span className="mku-hinweis__art">{HINWEIS_ART[x.art]}</span>
              <strong>{x.titel}</strong>
              <p>{x.text}</p>
            </article>
          ))}
        </div>
      )}
      <details className="mku-details">
        <summary>Wann entsteht ein Hinweis?</summary>
        <ul>
          <li>Abweichung größer als ±30 % bei vollständiger Vergleichsperiode und Basis ≥ 100 Sitzungen bzw. ≥ 20 Bestellungen; Kosten neutral, nicht automatisch gut/schlecht.</li>
          <li>Quelle veraltet: erwarteter Import plus 6 Stunden fehlt; GA4/Ads-Nachlauf bis T+3 getrennt als „vorläufig“ kennzeichnen. SEMrush erst nach dem Montagstermin prüfen.</li>
          <li>0-Zeilen: nur bei bestätigter Lieferung und früheren Messwerten. Fehlende Lieferung, Freigabe oder Historie ist nie eine gemessene Null.</li>
        </ul>
      </details>
    </section>
  );
}

function Quellen({ d }: { d: MktUebersicht }) {
  const an = quellenAngebunden(d.quellen);
  const wartend = quellenWartend(d.quellen);
  return (
    <footer className="pb-card mku-panel" aria-labelledby="mku-quellen">
      <Kopf id="mku-quellen" titel="Quellen & Datenstand" meta="Zeitzone Europe/Berlin" />
      <div className="mku-quellen">
        {an.map((q) => (
          <div key={q.system} className="mku-quelle">
            <strong>{q.name}</strong>
            <span className={`mku-badge mku-badge--${QUELLE_STATUS[q.status].ton}`}>{QUELLE_STATUS[q.status].text}</span>
            <p>
              {werteBisText(q)}{q.letzterTag && istVorlaeufig(q.letzterTag) ? ' · vorläufig' : ''}<br />
              {geladenText(q.datenstand)}
              {q.status === 'fehler' && <><br />Letzter Lauf fehlgeschlagen{q.lastError ? `: ${q.lastError}` : '.'}</>}
              {q.status !== 'fehler' && q.hinweis && <><br />{q.hinweis}</>}
            </p>
          </div>
        ))}
      </div>
      <p className="mku-fussnote">Bot-Bereinigung (GA4): keine Angabe geliefert.</p>
      <div className="mku-freigaben">
        {wartend.length > 0 && (
          <details id="mku-freigaben" className="mku-details">
            <summary>{plattformenWartenText(wartend.length)}</summary>
            <div className="mku-freigaben__liste">
              {wartend.map((q) => <div key={q.system}>{q.name}<small>{q.hinweis ?? 'Freigabe ausstehend.'}</small></div>)}
            </div>
          </details>
        )}
        <p className="mku-geplant"><span className="mku-badge">geplant</span> Search Console · noch keine Datenquelle für Brückner.</p>
      </div>
    </footer>
  );
}
